import { Alert, Button, Card, Popconfirm, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    Image as ZhImage
} from "zhihao-ui-react";
import { fileService } from "@/api/services/fileService";
import { useUserStore } from "@/views/stores";

import { loadEnterpriseDetail, type EnterpriseRow } from "./enterpriseManageAdapter";

export interface EnterpriseManageDetailPageProps {
    loadDetail?: (id: string) => Promise<EnterpriseRow>;
    loadFiles?: (ids: number[]) => Promise<Array<{ id?: number | string; path?: string }>>;
    deleteEnterprise?: (id: string) => Promise<void>;
    permissions?: string[];
}

async function loadEnterpriseFiles(ids: number[]) {
    return fileService.getFileByIds(ids);
}

/** 企业管理详情最小页面，沿用 Vue find/:id 接口和核心字段展示。 */
export function EnterpriseManageDetailPage({
    loadDetail = loadEnterpriseDetail,
    loadFiles = loadEnterpriseFiles,
    deleteEnterprise: remove,
    permissions
}: EnterpriseManageDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const id = new URLSearchParams(location.search).get("id");
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const canWrite = currentPermissions.includes("ROLE_SUPPLIER_DEAL");
    const [detail, setDetail] = useState<EnterpriseRow | null>(null);
    const [filePaths, setFilePaths] = useState<Record<string, string>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!id) {
            setErrorMessage("企业详情加载失败");
            return;
        }
        void loadDetail(id)
            .then(result => {
                setDetail(result);
                setErrorMessage(null);
                const fileIds = [
                    result.supplier?.logoFileId,
                    result.supplier?.businessFileId,
                    result.supplier?.mobileSideFileId,
                    result.supplier?.pcSideFileId
                ]
                    .filter(value => value != null)
                    .map(Number)
                    .filter(Number.isFinite);
                if (fileIds.length) {
                    void loadFiles(fileIds).then(files => {
                        setFilePaths(
                            files.reduce<Record<string, string>>((paths, file) => {
                                if (file.id != null && file.path) paths[String(file.id)] = file.path;
                                return paths;
                            }, {})
                        );
                    });
                }
            })
            .catch(() => setErrorMessage("企业详情加载失败"));
    }, [id, loadDetail, loadFiles]);

    const supplier = detail?.supplier;
    const renderFile = (fileId: number | string | undefined, alt: string) => {
        const path = fileId == null ? undefined : filePaths[String(fileId)];
        return path ? <ZhImage alt={alt} fit="contain" height="135px" src={path} width="240px" /> : fileId || "-";
    };

    return (
        <section aria-label="enterprise-detail-page">
            <Card>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {detail ? (
                    <>
                        <ZhDetailHeader
                            title="企业详情"
                            leftInfos={[{ label: "编号", value: supplier?.code || "-" }]}
                            rightInfos={[{ label: "创建时间", value: supplier?.createdTime || "-" }]}
                        />
                        <div className="section-title mb-4 mt-6">基本信息</div>
                        <ZhGrid className="mt-6" cols={4} gap={[30, 16]}>
                            <ZhBaseItem label="集团" value={supplier?.groupCompanyName || "-"} />
                            <ZhBaseItem label="企业名称" value={supplier?.supplierName || "-"} />
                            <ZhBaseItem label="类型" value={Number(supplier?.type) === 1 ? "企业" : "个人"} />
                            <ZhBaseItem
                                label="所在区域"
                                value={
                                    [
                                        [supplier?.province, supplier?.city, supplier?.region].filter(Boolean).join(""),
                                        supplier?.address
                                    ]
                                        .filter(Boolean)
                                        .join(" ") || "-"
                                }
                            />
                            <ZhBaseItem
                                label="展示位置"
                                value={Number(supplier?.showLocation) === 1 ? "首页" : "供应商列表"}
                            />
                            <ZhBaseItem label="企业标签" value={supplier?.labels?.join(";") || "-"} />
                            <ZhBaseItem label="联系人" value={supplier?.contactPerson || "-"} />
                            <ZhBaseItem label="联系电话" value={supplier?.contactPhone || "-"} />
                            <ZhBaseItem label="主营品类" span={4} value={supplier?.mainCategories || "-"} />
                            <ZhBaseItem label="统一社会信用代码" value={supplier?.socialCreditCode || "-"} />
                            <ZhBaseItem label="法人姓名" span={3} value={supplier?.legalRepresentative || "-"} />
                            <ZhBaseItem label="企业 Logo">{renderFile(supplier?.logoFileId, "企业 Logo")}</ZhBaseItem>
                            <ZhBaseItem label="营业执照">{renderFile(supplier?.businessFileId, "营业执照")}</ZhBaseItem>
                            <ZhBaseItem label="供应商首页广告-移动端">
                                {renderFile(supplier?.mobileSideFileId, "移动端首页广告")}
                            </ZhBaseItem>
                            <ZhBaseItem label="供应商首页广告-PC端">
                                {renderFile(supplier?.pcSideFileId, "PC端首页广告")}
                            </ZhBaseItem>
                            <ZhBaseItem label="企业介绍" span={4} value={supplier?.description || "-"} />
                        </ZhGrid>
                    </>
                ) : null}
                <Space className="mt-4">
                    {canWrite && remove ? (
                        <Popconfirm title="确认删除该供应商吗？" onConfirm={() => id && void remove(id)}>
                            <Button danger>删除</Button>
                        </Popconfirm>
                    ) : null}
                    <Button onClick={() => void navigate("/tradeManage/enterpriseManage")}>返回企业列表</Button>
                </Space>
            </Card>
        </section>
    );
}

export default EnterpriseManageDetailPage;
